import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Setup axios interceptor to always send token
const setupAxiosInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setupAxiosInterceptor();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password }
      );
      // Store tokens in localStorage as fallback for environments where cookies don't work
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      const msg = detail || (e.response ? 'Invalid email or password' : 'Unable to connect to server. Please try again.');
      return { success: false, error: msg };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name }
      );
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      const msg = detail || (e.response ? 'Registration failed' : 'Unable to connect to server. Please try again.');
      return { success: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('access_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
